/** Página de preços. Um objeto por língua com a mesma forma (como a página do produto). */
import type { Locale } from '@nolens/i18n';
import {
  AI_MONTHLY_LIMIT,
  annualSaving,
  maxAnnualSaving,
  PRICES,
  TEAM_MAX_EDITORS,
  TEAM_MIN_EDITORS,
  TEAM_TRIAL_DAYS,
  VAT_PT,
} from '@nolens/domain';
import { eur } from './landing';

type Cell = string | boolean;
type PlanId = 'free' | 'pro' | 'team';

export interface PlanCard {
  name: string;
  tagline: string;
  cta: string;
  /** «Principais funcionalidades» ou «Tudo do …, mais:». */
  includes: string;
  features: string[];
}

export interface PricingText {
  home: string;
  title: string;
  intro: string;
  yes: string;
  no: string;
  /** Faturação anual (ligada por omissão). */
  annual: string;
  annualSave: string;
  badge: string;
  /** Preço por mês (com faturação anual: o equivalente mensal). */
  price: (id: PlanId, yearly: boolean) => string;
  /** Unidade por baixo do preço: «mês» ou «editor / mês». */
  unit: (id: PlanId) => [string, string];
  /** Linhas por baixo do preço. */
  priceNotes: (id: PlanId, yearly: boolean) => string[];
  plans: Record<PlanId, PlanCard>;
  /** Destaque do plano Equipa (o que só ele tem). */
  teamHighlight: { title: string; text: string; items: string[] };
  payments: string;
  /** Mais do que o Equipa permite: contacto para um plano para empresas. */
  enterprise: string;
  compare: string;
  rows: { label: string; free: Cell; pro: Cell; team: Cell }[];
  trialEnd: string;
}

/** Equivalente mensal da faturação anual (ex.: 150 €/ano → 12,50 €/mês). */
const perMonth = (yearly: number) => yearly / 12;

/** Preço por mês do Pro mostrado no cartão (sem IVA). */
const proMonth = (yearly: boolean) => (yearly ? perMonth(PRICES.pro.yearly) : PRICES.pro.monthly);

/** Com o IVA português (os particulares têm de ver o preço final). */
const withVat = (n: number) => Math.round(n * (1 + VAT_PT) * 100) / 100;

const en = (): PricingText => ({
  home: 'Home',
  title: 'Help your team work faster',
  intro:
    'Each documented step saves, on average, about 2 minutes of screenshots, cropping and captions (an estimate). Prices exclude VAT, which is added at checkout.',
  yes: 'Yes',
  no: 'No',
  annual: 'Annual billing',
  annualSave: `Save up to ${maxAnnualSaving()}% with annual billing`,
  badge: 'Best value',
  price: (id, yearly) =>
    id === 'free'
      ? eur('en', 0)
      : eur('en', yearly ? perMonth(PRICES[id].yearly) : PRICES[id].monthly),
  unit: (id) => (id === 'team' ? ['editor', 'month'] : id === 'pro' ? ['', 'month'] : ['', '']),
  priceNotes: (id, yearly) =>
    id === 'free'
      ? ['Free forever, no card needed.']
      : id === 'pro'
        ? [
            'One person.',
            `+ VAT (${eur('en', withVat(proMonth(yearly)))}/month with Portuguese VAT).`,
            yearly
              ? `Billed yearly: ${eur('en', PRICES.pro.yearly)}/year.`
              : `Or ${eur('en', PRICES.pro.yearly)}/year (save ${annualSaving('pro')}%).`,
          ]
        : [
            `From ${eur('en', TEAM_MIN_EDITORS * (yearly ? perMonth(PRICES.team.yearly) : PRICES.team.monthly))}/month + VAT for ${TEAM_MIN_EDITORS} editors. Readers are free.`,
            yearly
              ? `Billed yearly: ${eur('en', PRICES.team.yearly)}/editor/year.`
              : `Or ${eur('en', PRICES.team.yearly)}/editor/year (save ${annualSaving('team')}%).`,
          ],
  plans: {
    free: {
      name: 'Free',
      tagline: 'Try Nolens Guides in your browser and create your first step-by-step guides.',
      cta: 'Start free',
      includes: 'Key features',
      features: [
        'Record with the extension, edit and guide (▶ Guide)',
        'Up to 10 guides',
        'PDF export',
        '500 MB for screenshots',
      ],
    },
    team: {
      name: 'Pro Team',
      tagline: 'Document processes for the whole company: onboarding, clients and new hires.',
      cta: 'Get Pro Team',
      includes: 'Everything in Pro, plus',
      features: [
        'Unlimited guides',
        'HTML, PowerPoint and Markdown export (editorial format)',
        'Public link and embedding in other pages',
        'Company branding (name, colors, logo, font)',
        '⚡ Run (the extension does the steps)',
        `AI text suggestions (${AI_MONTHLY_LIMIT} a month)`,
      ],
    },
    pro: {
      name: 'Pro',
      tagline: 'Create and share step-by-step guides for any process. Ideal for one person.',
      cta: 'Get Pro',
      includes: 'Everything in Free, plus',
      features: [
        'Unlimited guides',
        'HTML, PowerPoint and Markdown export (editorial format)',
        'Public link and embedding in other pages',
        'Company branding (name, colors, logo, font)',
        '⚡ Run (the extension does the steps)',
        `AI text suggestions (${AI_MONTHLY_LIMIT} a month)`,
        'Share with up to 3 readers',
        '20 GB for screenshots',
      ],
    },
  },
  teamHighlight: {
    title: 'Made for teams',
    text: 'Everyone works on the same guides, in one shared space.',
    items: [
      `${TEAM_MIN_EDITORS} to ${TEAM_MAX_EDITORS} editors`,
      'Unlimited readers, free',
      '10 GB of screenshots per editor (at least 30 GB)',
      'Team privacy rules: review before sending, keep typed text or not',
      `${TEAM_TRIAL_DAYS}-day trial if you already have Pro`,
    ],
  },
  payments:
    'Payments are processed securely by Paddle, our reseller. Cancel at any time; full refund within 14 days.',
  enterprise: `More than ${TEAM_MAX_EDITORS} editors, a contract or a data processing agreement? Talk to us about a company plan:`,
  compare: 'Compare the plans',
  rows: [
    {
      label: 'Record with the extension, edit and guide (▶ Guide)',
      free: true,
      pro: true,
      team: true,
    },
    { label: 'Guides', free: 'up to 10', pro: 'unlimited', team: 'unlimited' },
    { label: 'PDF export', free: true, pro: true, team: true },
    {
      label: 'HTML, PowerPoint and Markdown export (editorial format)',
      free: false,
      pro: true,
      team: true,
    },
    { label: 'Public link and embedding in other pages', free: false, pro: true, team: true },
    { label: 'Company branding (name, colors, logo, font)', free: false, pro: true, team: true },
    { label: '⚡ Run (the extension does the steps)', free: false, pro: true, team: true },
    {
      label: 'AI text suggestions',
      free: false,
      pro: `${AI_MONTHLY_LIMIT} a month`,
      team: `${AI_MONTHLY_LIMIT} a month per team`,
    },
    {
      label: 'Editors',
      free: 'just you',
      pro: 'just you',
      team: `${TEAM_MIN_EDITORS} to ${TEAM_MAX_EDITORS} (paid)`,
    },
    { label: 'Invited readers', free: '—', pro: 'up to 3', team: 'unlimited, free' },
    {
      label: 'Screenshot storage',
      free: '500 MB',
      pro: '20 GB',
      team: '10 GB per editor (min. 30 GB)',
    },
    {
      label: 'Personal data (email, phone, tax and bank numbers, cards) covered before sending',
      free: true,
      pro: true,
      team: true,
    },
    { label: 'Data stored in the EU (Frankfurt)', free: true, pro: true, team: true },
    {
      label: 'Required review before sending, for the whole team',
      free: false,
      pro: false,
      team: true,
    },
  ],
  trialEnd:
    'When a team has no active plan (trial over or subscription ended), its guides stay visible and can be deleted; they just can’t be created or changed until you choose a plan.',
});

const ptPT = (): PricingText => ({
  home: 'Início',
  title: 'Ajude a sua equipa a trabalhar mais depressa',
  intro:
    'Cada passo documentado poupa, em média, cerca de 2 minutos de capturas, recortes e legendas (estimativa). Preços sem IVA: o IVA é somado no pagamento.',
  yes: 'Sim',
  no: 'Não',
  annual: 'Faturação anual',
  annualSave: `Poupe até ${maxAnnualSaving()} % com a faturação anual`,
  badge: 'Melhor valor',
  price: (id, yearly) =>
    id === 'free'
      ? eur('pt-PT', 0)
      : eur('pt-PT', yearly ? perMonth(PRICES[id].yearly) : PRICES[id].monthly),
  unit: (id) => (id === 'team' ? ['editor', 'mês'] : id === 'pro' ? ['', 'mês'] : ['', '']),
  priceNotes: (id, yearly) =>
    id === 'free'
      ? ['Grátis para sempre, sem cartão.']
      : id === 'pro'
        ? [
            'Uma pessoa.',
            `+ IVA (${eur('pt-PT', withVat(proMonth(yearly)))}/mês com o IVA português).`,
            yearly
              ? `Faturado anualmente: ${eur('pt-PT', PRICES.pro.yearly)}/ano.`
              : `Ou ${eur('pt-PT', PRICES.pro.yearly)}/ano (poupa ${annualSaving('pro')} %).`,
          ]
        : [
            `A partir de ${eur('pt-PT', TEAM_MIN_EDITORS * (yearly ? perMonth(PRICES.team.yearly) : PRICES.team.monthly))}/mês + IVA para ${TEAM_MIN_EDITORS} editores. Leitores grátis.`,
            yearly
              ? `Faturado anualmente: ${eur('pt-PT', PRICES.team.yearly)}/editor/ano.`
              : `Ou ${eur('pt-PT', PRICES.team.yearly)}/editor/ano (poupa ${annualSaving('team')} %).`,
          ],
  plans: {
    free: {
      name: 'Grátis',
      tagline:
        'Experimente o Nolens Guides no seu browser e crie os primeiros guias passo a passo.',
      cta: 'Começar grátis',
      includes: 'Principais funcionalidades',
      features: [
        'Gravar com a extensão, editar e guiar (▶ Guiar)',
        'Até 10 guias',
        'Exportar PDF',
        '500 MB para capturas',
      ],
    },
    team: {
      name: 'Pro Equipa',
      tagline:
        'Documente os processos de toda a empresa: integração de pessoas novas, clientes e equipas.',
      cta: 'Quero o Pro Equipa',
      includes: 'Tudo o que o Pro tem, e ainda',
      features: [
        'Guias ilimitados',
        'Exportar HTML, PowerPoint e Markdown (formato editorial)',
        'Ligação pública e incorporar noutras páginas',
        'Marca da empresa (nome, cores, logótipo, letra)',
        '⚡ Executar (a extensão faz os passos)',
        `Sugestões de texto por IA (${AI_MONTHLY_LIMIT} por mês)`,
      ],
    },
    pro: {
      name: 'Pro',
      tagline: 'Crie e partilhe guias passo a passo de qualquer processo. Ideal para uma pessoa.',
      cta: 'Quero o Pro',
      includes: 'Tudo o que o Grátis tem, e ainda',
      features: [
        'Guias ilimitados',
        'Exportar HTML, PowerPoint e Markdown (formato editorial)',
        'Ligação pública e incorporar noutras páginas',
        'Marca da empresa (nome, cores, logótipo, letra)',
        '⚡ Executar (a extensão faz os passos)',
        `Sugestões de texto por IA (${AI_MONTHLY_LIMIT} por mês)`,
        'Partilhar com até 3 leitores',
        '20 GB para capturas',
      ],
    },
  },
  teamHighlight: {
    title: 'Feito para equipas',
    text: 'Todos trabalham nos mesmos guias, num espaço partilhado.',
    items: [
      `De ${TEAM_MIN_EDITORS} a ${TEAM_MAX_EDITORS} editores`,
      'Leitores ilimitados e grátis',
      '10 GB de capturas por editor (no mínimo 30 GB)',
      'Regras de privacidade da equipa: rever antes de enviar, guardar ou não o texto escrito',
      `${TEAM_TRIAL_DAYS} dias de teste para quem já tem o Pro`,
    ],
  },
  payments:
    'Os pagamentos são processados com segurança pelo Paddle, o nosso revendedor. Cancele quando quiser; reembolso total até 14 dias.',
  enterprise: `Mais de ${TEAM_MAX_EDITORS} editores, contrato ou acordo de tratamento de dados? Fale connosco sobre um plano para empresas:`,
  compare: 'Comparar os planos',
  rows: [
    { label: 'Gravar com a extensão, editar e guiar (▶ Guiar)', free: true, pro: true, team: true },
    { label: 'Guias', free: 'até 10', pro: 'ilimitados', team: 'ilimitados' },
    { label: 'Exportar PDF', free: true, pro: true, team: true },
    {
      label: 'Exportar HTML, PowerPoint e Markdown (formato editorial)',
      free: false,
      pro: true,
      team: true,
    },
    { label: 'Ligação pública e incorporar noutras páginas', free: false, pro: true, team: true },
    {
      label: 'Marca da empresa (nome, cores, logótipo, letra)',
      free: false,
      pro: true,
      team: true,
    },
    { label: '⚡ Executar (a extensão faz os passos)', free: false, pro: true, team: true },
    {
      label: 'Sugestões de texto por IA',
      free: false,
      pro: `${AI_MONTHLY_LIMIT} por mês`,
      team: `${AI_MONTHLY_LIMIT} por mês por equipa`,
    },
    {
      label: 'Editores',
      free: 'só você',
      pro: 'só você',
      team: `${TEAM_MIN_EDITORS} a ${TEAM_MAX_EDITORS} (pagos)`,
    },
    { label: 'Leitores convidados', free: '—', pro: 'até 3', team: 'ilimitados, grátis' },
    {
      label: 'Espaço para capturas',
      free: '500 MB',
      pro: '20 GB',
      team: '10 GB por editor (mín. 30 GB)',
    },
    {
      label: 'Dados pessoais (email, telefone, NIF, IBAN, cartões) tapados antes do envio',
      free: true,
      pro: true,
      team: true,
    },
    { label: 'Dados guardados na UE (Frankfurt)', free: true, pro: true, team: true },
    {
      label: 'Rever antes de enviar obrigatório para toda a equipa',
      free: false,
      pro: false,
      team: true,
    },
  ],
  trialEnd:
    'Quando o teste de uma equipa termina sem plano, os guias continuam visíveis e podem ser apagados; só não se criam nem alteram até escolher um plano.',
});

const PRICING: Record<Locale, () => PricingText> = { en, 'pt-PT': ptPT };
export const pricingText = (l: Locale): PricingText => PRICING[l]();
